import { useCallback, useRef } from 'react';
import _get from 'lodash/get';
import { ErrorLike, WatchQueryFetchPolicy } from '@apollo/client';
import {
  TaskCompletion,
  TaskCompletionFilterInputObject,
  TaskCompletionScopeNameEnum,
  TaskCompletionsQueryVariables,
  useTaskCompletionsQuery,
  useTaskCompletionsIdsLazyQuery,
} from '@lib/graphql-common';
import {
  GetUrlParams,
  OrderingParams,
  PaginationParams,
} from '@lib/enums/urls';
import { FetchIdsResponse } from '@lib/utils/getAllTableItemsIds';
import useTaskAccess from '@lib/hooks/useTaskAccess';

type Props = {
  additionalFilters?: object[];
  fetchPolicy?: WatchQueryFetchPolicy;
  groupKey: string;
  orderingParams?: OrderingParams;
  paginationParams?: PaginationParams;
  scope?: TaskCompletionScopeNameEnum;
  skip?: boolean;
  usePrevious?: boolean;
};

export type UseTaskCompletionsQueryHookResult = {
  collection: TaskCompletion[];
  error: ErrorLike | undefined;
  fetchIds: () => Promise<FetchIdsResponse<unknown>>;
  firstLoading: boolean;
  loading: boolean;
  refetch: () => Promise<unknown>;
  totalCount: number;
  totalPages: number;
};

function toNumber(value?: string | number | null) {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export default function useTaskCompletionsQueryHook({
  additionalFilters,
  fetchPolicy = 'cache-and-network',
  groupKey,
  orderingParams,
  paginationParams,
  scope,
  skip,
  usePrevious = true,
}: Props): UseTaskCompletionsQueryHookResult {
  const pageParam = _get(paginationParams, GetUrlParams.Page);
  const perPageParam = _get(paginationParams, GetUrlParams.PerPage);
  const orderingSource = orderingParams || paginationParams;
  const orderingField = _get(orderingSource, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    orderingSource,
    GetUrlParams.OrderingDirection,
  );

  const page = toNumber(pageParam);
  const limit = toNumber(perPageParam);

  const { withIntervention } = useTaskAccess();

  const variables: TaskCompletionsQueryVariables = {
    groupKey,
    withIntervention,
  };

  if (scope) {
    variables.scope = scope;
  }

  if (page !== undefined) {
    variables.page = page;
  }
  if (limit !== undefined) {
    variables.limit = limit;
  }

  const filters = (additionalFilters ||
    []) as TaskCompletionFilterInputObject[];
  if (filters.length) {
    variables.filters = filters;
  }

  if (orderingField && orderingDirection) {
    variables.sorts = {
      [orderingField]: orderingDirection,
    };
  }

  const { data, previousData, loading, error, refetch } =
    useTaskCompletionsQuery({
      fetchPolicy,
      variables,
      skip,
    });

  const [fetchIdsQuery] = useTaskCompletionsIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  // Stable reference so consumers can safely use it as a useEffect dependency
  // without triggering an infinite render loop.
  const fetchIds = useCallback(
    () =>
      fetchIdsQuery({
        variables: {
          groupKey,
          scope,
          filters:
            additionalFilters && additionalFilters.length
              ? (additionalFilters as TaskCompletionFilterInputObject[])
              : undefined,
          sorts:
            orderingField && orderingDirection
              ? { [orderingField]: orderingDirection }
              : undefined,
        },
      }),
    [
      fetchIdsQuery,
      groupKey,
      scope,
      additionalFilters,
      orderingField,
      orderingDirection,
    ],
  );

  const queryData = usePrevious ? data || previousData : data;

  // `previousData` survives a `groupKey` change (this hook's owner doesn't
  // remount between tasks — see TaskCompletionsModal), so relying on it
  // alone to detect "first load" misfires whenever the previous task's
  // result happened to be empty: `previousData` is then a defined (empty)
  // array, `firstLoading` reads false, and the table renders the previous
  // task's stale/empty collection instead of the loading state. Track which
  // groupKey the settled data actually belongs to and force `firstLoading`
  // until the new group's fetch resolves.
  const resolvedGroupKeyRef = useRef<string | undefined>(undefined);
  if (!loading && data !== undefined) {
    resolvedGroupKeyRef.current = groupKey;
  }
  const isDifferentGroup =
    resolvedGroupKeyRef.current !== undefined &&
    resolvedGroupKeyRef.current !== groupKey;
  const firstLoading =
    loading && (previousData === undefined || isDifferentGroup);

  const collection =
    !usePrevious && loading
      ? []
      : (_get(queryData, 'data.collection', []) as TaskCompletion[]);
  const metadata = _get(queryData, 'data.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 0);

  return {
    collection,
    error,
    fetchIds,
    firstLoading,
    loading,
    refetch,
    totalCount,
    totalPages,
  };
}
